import { defineStore } from 'pinia';
import {
	DriverStatus,
	type Driver,
	type GeoPoint,
	type OfferReceivedPayload,
	type Ride,
	type RideOffer,
} from '@taxi/shared';
import { ApiException } from '@common/index';

// `RideOffer.ride` est optionnel dans le contrat d'API ; ici il est toujours
// présent (éventuellement nul), d'où le retrait avant redéclaration.
export interface PendingOffer extends Omit<RideOffer, 'ride'> {
	ride: Ride | null;
	/** Secondes restantes pour répondre, décomptées localement. */
	remainingSeconds: number;
}

export interface DriverState {
	profile: Driver | null;
	offers: PendingOffer[];
	currentRide: Ride | null;
	/** Horodatage du dernier envoi — sert à espacer et à relancer. */
	lastSentAt: number;
	/**
	 * Étapes de course non transmises, faute de réseau.
	 *
	 * Le chauffeur traverse des tunnels et des parkings : ce qu'il a déclaré ne
	 * doit pas se perdre parce que le réseau manquait à cet instant.
	 */
	pending: { rideId: number; step: 'arrived' | 'pickup' | 'complete'; at: number }[];
	/** Dernière position transmise à l'API — évite les envois redondants. */
	lastSentPosition: GeoPoint | null;
	loading: boolean;
}

/** En deçà de ce déplacement, inutile de solliciter l'API : le marqueur ne bougerait pas. */
const MIN_MOVE_METERS = 15;

/**
 * Silence maximal entre deux envois, en millisecondes.
 *
 * Un chauffeur à l'arrêt ne franchit jamais le seuil de déplacement : sans cette
 * relance, sa dernière position vieillirait jusqu'à être jugée périmée par le
 * dispatch, et il sortirait de la liste des taxis disponibles sans avoir bougé.
 */
const MAX_SILENCE_MS = 60_000;

/**
 * Intervalle minimal entre deux envois, en millisecondes.
 *
 * En roulant, le GPS produit un point par seconde : chacun franchit le seuil de
 * quinze mètres dès 55 km/h. Transmettre à cette cadence tiendrait la radio du
 * téléphone allumée en permanence — c'est le premier poste de consommation d'une
 * application de conduite, bien avant l'écran.
 */
const MIN_INTERVAL_MS = 10_000;

/**
 * Mode conduite.
 *
 * Concentre ce que le chauffeur pilote : sa disponibilité, sa position transmise
 * en continu, les propositions reçues et l'avancement de sa course.
 */
export const useDriverStore = defineStore('driver', {
	state: (): DriverState => ({
		profile: null,
		offers: [],
		currentRide: null,
		lastSentPosition: null,
		lastSentAt: 0,
		pending: [],
		loading: false,
	}),

	getters: {
		status: (state): DriverStatus => state.profile?.status ?? DriverStatus.OFFLINE,

		isOnline: (state): boolean =>
			!!state.profile && state.profile.status !== DriverStatus.OFFLINE,

		/** Disponible au sens du dispatch : ni en course, ni en pause. */
		isAvailable: (state): boolean => state.profile?.status === DriverStatus.AVAILABLE,

		hasRide: (state): boolean => !!state.currentRide,

		/** Proposition la plus proche, présentée en premier au chauffeur. */
		topOffer: (state): PendingOffer | null =>
			state.offers.length
				? [...state.offers].sort((a, b) => a.distanceMeters - b.distanceMeters)[0]
				: null,
	},

	actions: {
		// -- Profil -------------------------------------------------------------

		async loadSelf(): Promise<Driver | null> {
			this.loading = true;
			try {
				this.profile = await this.$caller().get<Driver>('/drivers/me');
				return this.profile;
			} finally {
				this.loading = false;
			}
		},

		async updateProfile(payload: Record<string, unknown>): Promise<Driver> {
			this.profile = await this.$caller().patch<Driver>('/drivers/me', payload);
			return this.profile;
		},

		// -- Disponibilité ------------------------------------------------------

		async setStatus(status: DriverStatus): Promise<Driver> {
			this.profile = await this.$caller().post<Driver>('/drivers/me/status', { status });
			return this.profile;
		},

		goOnline(): Promise<Driver> {
			return this.setStatus(DriverStatus.AVAILABLE);
		},

		goOffline(): Promise<Driver> {
			return this.setStatus(DriverStatus.OFFLINE);
		},

		// -- Position -----------------------------------------------------------

		/**
		 * Transmet la position à l'API.
		 *
		 * Trois filtres, dans cet ordre — c'est ce qui décide de l'autonomie d'un
		 * téléphone posé sur un tableau de bord toute la journée :
		 *
		 *  1. **cadence** — jamais plus d'un envoi toutes les dix secondes ; en
		 *     roulant, le GPS en produit un par seconde et chacun franchirait le
		 *     seuil de distance ;
		 *  2. **déplacement** — en deçà de quinze mètres, le marqueur ne bougerait
		 *     pas à l'écran du client ;
		 *  3. **silence** — mais au-delà d'une minute sans nouvelle, on transmet
		 *     quand même : une position qui vieillit est jugée périmée par le
		 *     dispatch, et le chauffeur sortirait de la liste sans avoir bougé.
		 *
		 * `force` court-circuite les trois : c'est la prise de service, où la
		 * position doit partir *avant* le changement de statut.
		 */
		async pushPosition(position: GeoPoint, force = false): Promise<void> {
			// Hors service, on ne transmet rien : suivre un chauffeur qui a terminé sa
			// journée serait indéfendable. Seule exception, l'envoi forcé de la prise
			// de service — l'API refuse « disponible » tant qu'elle ignore où il est.
			if (!this.isOnline && !force) {
				return;
			}

			const now = Date.now();
			const silent = now - this.lastSentAt;

			if (!force && this.lastSentPosition && silent < MIN_INTERVAL_MS) {
				return;
			}
			if (
				!force &&
				this.lastSentPosition &&
				this.distanceTo(position) < MIN_MOVE_METERS &&
				silent < MAX_SILENCE_MS
			) {
				return;
			}
			try {
				this.profile = await this.$caller().post<Driver>(
					'/drivers/me/position',
					{
						lat: position.lat,
						lng: position.lng,
						heading: position.heading ?? undefined,
						speed: position.speed ?? undefined,
						accuracy: position.accuracy ?? undefined,
					},
					{ silentError: true },
				);
				this.lastSentPosition = position;
				this.lastSentAt = now;
			} catch (error) {
				// Perte de réseau passagère : le prochain point sera transmis. Un envoi
				// forcé, lui, conditionne la prise de service : son échec doit s'afficher
				// plutôt que de laisser l'API refuser ensuite pour « position inconnue ».
				if (force) {
					throw error;
				}
			}
		},

		distanceTo(position: GeoPoint): number {
			const previous = this.lastSentPosition;
			if (!previous) {
				return Number.POSITIVE_INFINITY;
			}
			// Approximation plane suffisante à cette échelle (quelques centaines
			// de mètres) et bien moins coûteuse qu'un haversine à chaque point GPS.
			const dLat = (position.lat - previous.lat) * 111_000;
			const dLng =
				(position.lng - previous.lng) * 111_000 * Math.cos((position.lat * Math.PI) / 180);
			return Math.sqrt(dLat * dLat + dLng * dLng);
		},

		// -- Propositions -------------------------------------------------------

		async loadOffers(): Promise<PendingOffer[]> {
			const offers = await this.$caller().get<(RideOffer & { ride: Ride | null })[]>(
				'/drivers/me/offers',
				{ silentError: true },
			);
			this.offers = offers.map((offer) => ({
				...offer,
				remainingSeconds: this.remainingFor(offer.expiresAt),
			}));
			return this.offers;
		},

		/** Réception temps réel d'une proposition. */
		receiveOffer(payload: OfferReceivedPayload): void {
			if (this.offers.some((offer) => offer.id === payload.offer.id)) {
				return;
			}
			this.offers.push({
				...payload.offer,
				ride: payload.ride,
				remainingSeconds: payload.ttlSeconds,
			});
		},

		closeOffer(offerId: number): void {
			this.offers = this.offers.filter((offer) => offer.id !== offerId);
		},

		/** Décompte local du délai de réponse, appelé chaque seconde par la vue. */
		tickOffers(): void {
			this.offers = this.offers
				.map((offer) => ({
					...offer,
					remainingSeconds: this.remainingFor(offer.expiresAt),
				}))
				.filter((offer) => offer.remainingSeconds > 0);
		},

		async acceptOffer(rideId: number): Promise<Ride> {
			const ride = await this.$caller().post<Ride>(
				`/drivers/me/offers/${rideId}/accept`,
				undefined,
				{ silentError: true },
			);
			this.currentRide = ride;
			this.offers = [];
			await this.loadSelf();
			return ride;
		},

		async declineOffer(rideId: number): Promise<void> {
			await this.$caller().post(`/drivers/me/offers/${rideId}/decline`);
			this.offers = this.offers.filter((offer) => offer.rideId !== rideId);
		},

		// -- Avancement de la course --------------------------------------------

		async markArrived(): Promise<Ride | null> {
			return this.progress('arrived');
		},

		/** « Client récupéré » : le chauffeur passe en charge et le trajet démarre. */
		async markPickedUp(): Promise<Ride | null> {
			return this.progress('pickup');
		},

		async markCompleted(): Promise<Ride | null> {
			const ride = await this.progress('complete');
			this.currentRide = null;
			return ride;
		},

		/**
		 * Fait avancer la course d'une étape.
		 *
		 * Un chauffeur monégasque perd le réseau plusieurs fois par jour : tunnel
		 * du Rocher, parkings souterrains, sous-sol du Grimaldi Forum. Une étape
		 * qui échouerait là serait perdue — le client resterait en « chauffeur en
		 * approche » alors qu'il est déjà à bord.
		 *
		 * L'étape est donc **mise en attente** quand le réseau manque, et rejouée
		 * dès qu'il revient. L'écran du chauffeur avance immédiatement : c'est lui
		 * qui sait ce qui s'est passé, pas le serveur.
		 */
		async progress(step: 'arrived' | 'pickup' | 'complete'): Promise<Ride | null> {
			if (!this.currentRide) {
				return null;
			}

			const rideId = this.currentRide.id;
			try {
				const ride = await this.$caller().post<Ride>(
					`/drivers/me/rides/${rideId}/${step}`,
					undefined,
					{ silentError: true },
				);
				this.currentRide = ride;
				await this.loadSelf();
				return ride;
			} catch (error) {
				// Refus métier — étape déjà franchie, course annulée : rejouer n'y
				// changerait rien, l'erreur remonte à l'écran.
				if (error instanceof ApiException && error.status > 0 && error.status < 500) {
					throw error;
				}

				this.pending.push({ rideId, step, at: Date.now() });
				return this.currentRide;
			}
		},

		/**
		 * Rejoue les étapes mises en attente.
		 *
		 * Appelée au retour du réseau. L'ordre est conservé : « client à bord »
		 * avant « terminée », sans quoi l'API refuserait la seconde.
		 */
		async flushPending(): Promise<void> {
			if (!this.pending.length) {
				return;
			}

			const queue = [...this.pending];
			this.pending = [];

			for (const entry of queue) {
				try {
					const ride = await this.$caller().post<Ride>(
						`/drivers/me/rides/${entry.rideId}/${entry.step}`,
						undefined,
						{ silentError: true },
					);
					if (this.currentRide?.id === ride.id) {
						this.currentRide = ride;
					}
				} catch (error) {
					// Toujours hors réseau : on remet en file et on s'arrête là, pour
					// ne pas jouer une étape ultérieure avant celle qui la précède.
					if (!(error instanceof ApiException) || error.status >= 500) {
						this.pending = [entry, ...queue.slice(queue.indexOf(entry) + 1)];
						return;
					}
					// Refus métier : l'étape n'a plus lieu d'être, on l'abandonne.
				}
			}

			await this.loadSelf();
		},

		applyRideUpdate(ride: Ride): void {
			if (this.profile && ride.driverId === this.profile.id) {
				this.currentRide = ride;
			}
		},

		remainingFor(expiresAt: string): number {
			return Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000));
		},
	},
});
